import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { AppState, AppStateStatus } from "react-native"

import { Alert } from "src/components/Alert"
import { useConversationRejectedStatus } from "src/queries/useConversation"
import { useCleanupThenLogout } from "src/queries/useUser"
import { useChat } from "./ChatContext"
import { useMicrophone } from "./MicrophoneContext"
import { useTyping } from "./TypingContext"
import { useVisemes } from "./VisemeContext"
import { useWebsocket } from "./WebsocketContext"

/**
 * User input modes
 */
export type InputMode = "idle" | "talk" | "type"

interface SidekickInteractionContextType {
  // === Connection State ===
  isConnected: boolean
  isReconnecting: boolean

  // === Core Interaction State ===
  inputMode: InputMode
  isMicLoading: boolean
  isListening: boolean
  isProcessing: boolean
  isTransferring: boolean
  isSwitchingSidekick: boolean
  isSidekickBusy: boolean

  // === Derived State ===
  canStartNewInteraction: boolean
  shouldShowAvatar: boolean
  /** True for a short period after processing ends (e.g. before transfer); UI should still show processing state */
  inTransferGracePeriod: boolean

  // === Actions ===
  startTalkMode: () => void
  startTypeMode: () => void
  returnToIdle: () => void
}

export const SidekickInteractionContext = createContext<SidekickInteractionContextType | undefined>(
  undefined,
)

/**
 * Provider component that manages all user-sidekick interaction state
 */
export function SidekickInteractionProvider({ children }: { children: React.ReactNode }) {
  // Pull from existing contexts
  const { isListening, isLoading: isMicHardwareLoading } = useMicrophone()
  const { awaitingUserSentence, awaitingESKResponse, setAwaitingESKResponse } = useTyping()
  const { isAudioExpected, isSpeaking } = useVisemes()
  const {
    isSwitchingSidekick,
    waitingForWelcomeMessage,
    startConversationStatus,
    startConversationTimedOut,
    clearStartConversationTimedOut,
    startNewConversation,
    isSidekickBusy,
    cancelMicStream,
    cancelWaitingForWelcomeMessage,
    stopMicStream,
    audioStreamingReady,
  } = useChat()
  const { data: conversationRejectedStatus } = useConversationRejectedStatus()
  const { mutate: cleanupThenLogout } = useCleanupThenLogout()
  const { isConnected } = useWebsocket()

  // Local state
  const [inputMode, setInputMode] = useState<InputMode>("idle")
  const [isMicLoading, setIsMicLoading] = useState(false)

  // Clear awaitingESKResponse when audio starts playing (we have the response).
  useEffect(() => {
    if (awaitingESKResponse && isSpeaking && !isListening) {
      setAwaitingESKResponse(false)
    }
  }, [awaitingESKResponse, isSpeaking, isListening, setAwaitingESKResponse])

  // Reconnecting = not connected (websocket handles auto-reconnect)
  const isReconnecting = !isConnected

  // Clear mic loading when hardware loading completes AND backend is ready
  useEffect(() => {
    if (!isMicHardwareLoading && audioStreamingReady) {
      setIsMicLoading(false)
    }
  }, [isMicHardwareLoading, audioStreamingReady])

  // Auto-cancel on disconnect during active interaction
  useEffect(() => {
    if (!isConnected && (isListening || isMicLoading)) {
      cancelMicStream()
      stopMicStream()
      setIsMicLoading(false)
      setInputMode("idle")
    }
  }, [isConnected, isListening, isMicLoading, cancelMicStream, stopMicStream])

  // When app goes to background or inactive while in talk mode, cancel and return to idle immediately
  // so the mic stops listening and we don't send audio on a dying socket (state machine: App background/inactive → Idle).
  // React to "inactive" as well as "background" so we stop sooner when the device is locked.
  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState: AppStateStatus) => {
      if ((nextAppState === "background" || nextAppState === "inactive") && inputMode === "talk") {
        cancelMicStream()
        stopMicStream()
        setIsMicLoading(false)
        setInputMode("idle")
      }
    })
    return () => subscription.remove()
  }, [inputMode, cancelMicStream, stopMicStream])

  // Safety watchdog: if isMicLoading stays true for too long without entering listening,
  // treat it as a stuck state and force-clean. This catches every cause of "spinner spins
  // forever" — startStream() never running (e.g. autoStart blocked by stale isProcessing
  // after wake-from-sleep), voiceProcessor hanging silently, the backend never sending
  // streaming_ready, etc. We also cancel any stale "waiting for welcome message" wait
  // because that flag pins isProcessing=true, which would otherwise keep autoStart from
  // firing on the user's next Talk press and re-trigger the same stuck loop.
  const MIC_LOADING_WATCHDOG_MS = 8000
  useEffect(() => {
    if (!isMicLoading || isListening) return undefined
    const timer = setTimeout(() => {
      console.warn(
        `[SidekickInteractionContext] isMicLoading exceeded ${MIC_LOADING_WATCHDOG_MS}ms without entering listening; forcing return to idle.`,
      )
      cancelMicStream()
      stopMicStream()
      cancelWaitingForWelcomeMessage()
      setIsMicLoading(false)
      setInputMode("idle")
    }, MIC_LOADING_WATCHDOG_MS)
    return () => clearTimeout(timer)
  }, [isMicLoading, isListening, cancelMicStream, stopMicStream, cancelWaitingForWelcomeMessage])

  // If the server never responds to start_conversation, unblock the UI and let the user retry or log out.
  useEffect(() => {
    if (!startConversationTimedOut) return
    Alert.alert(
      "Server not responding",
      "Sidekick didn't respond in time. You can try again or log out.",
      [
        {
          text: "Retry",
          onPress: () => {
            clearStartConversationTimedOut()
            startNewConversation()
          },
        },
        {
          text: "Log out",
          style: "destructive",
          onPress: () => {
            clearStartConversationTimedOut()
            cleanupThenLogout()
          },
        },
      ],
    )
  }, [
    startConversationTimedOut,
    clearStartConversationTimedOut,
    startNewConversation,
    cleanupThenLogout,
  ])

  // Computed values
  const isTransferring = isSwitchingSidekick || startConversationStatus?.isPending

  // isProcessing tracks backend processing (text/audio) but allows interruption once speaking
  // awaitingESKResponse is cleared when text is done, which may be before audio plays
  // So we add isAudioExpected to cover the gap, and check isSpeaking to allow interruption
  const isProcessing =
    isTransferring ||
    waitingForWelcomeMessage ||
    awaitingUserSentence ||
    (awaitingESKResponse && !isSpeaking) || // Block until text done, unless already speaking (allow interruption)
    (isAudioExpected && !isSpeaking) // Block while audio being received AND not yet playing

  // Brief grace period after response completes so we don't enable the button cluster
  // before start_conversation/SwitchCM arrives (e.g. after sidekick says "Sure." to a transfer request).
  const [inTransferGracePeriod, setInTransferGracePeriod] = useState(false)
  const gracePeriodTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wasProcessingRef = useRef(false)
  const TRANSFER_GRACE_MS = 1000

  // Block in the "processing just finished" frame: inTransferGracePeriod is set in an effect
  // (after commit), so we'd otherwise render one frame with the cluster enabled.
  const justFinishedProcessing = wasProcessingRef.current && !isProcessing

  // THE KEY DERIVED STATE
  // Blocks during processing (and grace period) so we don't briefly enable before a transfer
  const canStartNewInteraction =
    isConnected &&
    !isMicLoading &&
    !isListening &&
    !isProcessing &&
    !inTransferGracePeriod &&
    !justFinishedProcessing &&
    !conversationRejectedStatus?.isRejected

  // Avatar visibility - Sidekick NEVER disappears completely
  // It is always in one of three states: large, minimized, or tab (hidden)
  // The avatar mode system handles visibility, not this flag
  const shouldShowAvatar = true

  // When processing completes: start transfer grace period; reset to idle if in talk mode
  useEffect(() => {
    const wasProcessing = wasProcessingRef.current
    wasProcessingRef.current = isProcessing

    if (wasProcessing && !isProcessing) {
      if (!isListening && inputMode === "talk") {
        setInputMode("idle")
      }
      if (gracePeriodTimeoutRef.current) clearTimeout(gracePeriodTimeoutRef.current)
      setInTransferGracePeriod(true)
      gracePeriodTimeoutRef.current = setTimeout(() => {
        gracePeriodTimeoutRef.current = null
        setInTransferGracePeriod(false)
      }, TRANSFER_GRACE_MS)
    }
    if (isProcessing && inTransferGracePeriod) {
      setInTransferGracePeriod(false)
      if (gracePeriodTimeoutRef.current) {
        clearTimeout(gracePeriodTimeoutRef.current)
        gracePeriodTimeoutRef.current = null
      }
    }
  }, [isProcessing, isListening, inputMode, inTransferGracePeriod])

  useEffect(() => {
    return () => {
      if (gracePeriodTimeoutRef.current) clearTimeout(gracePeriodTimeoutRef.current)
    }
  }, [])

  // Actions
  const startTalkMode = useCallback(() => {
    if (!isConnected) return // Prevent starting if disconnected
    setInputMode("talk")
    setIsMicLoading(true)
  }, [isConnected])

  const startTypeMode = useCallback(() => {
    setInputMode("type")
  }, [])

  const returnToIdle = useCallback(() => {
    setInputMode("idle")
    setIsMicLoading(false)
  }, [])

  const value = useMemo(
    () => ({
      // Connection State
      isConnected,
      isReconnecting,
      // Core Interaction State
      inputMode,
      isMicLoading,
      isListening,
      isProcessing,
      isTransferring,
      isSwitchingSidekick,
      isSidekickBusy,
      // Derived State
      canStartNewInteraction,
      shouldShowAvatar,
      inTransferGracePeriod,
      // Actions
      startTalkMode,
      startTypeMode,
      returnToIdle,
    }),
    [
      isConnected,
      isReconnecting,
      inputMode,
      isMicLoading,
      isListening,
      isProcessing,
      isTransferring,
      isSwitchingSidekick,
      isSidekickBusy,
      canStartNewInteraction,
      shouldShowAvatar,
      inTransferGracePeriod,
      startTalkMode,
      startTypeMode,
      returnToIdle,
    ],
  )

  return (
    <SidekickInteractionContext.Provider value={value}>
      {children}
    </SidekickInteractionContext.Provider>
  )
}

/**
 * Hook to access and update the sidekick interaction state
 */
export function useSidekickInteraction() {
  const context = useContext(SidekickInteractionContext)
  if (context === undefined) {
    throw new Error("useSidekickInteraction must be used within a SidekickInteractionProvider")
  }
  return context
}
